import React, { useState } from "react";
import {
  Form,
  Select,
  Button,
  Modal,
  message,
  Row,
  Col,
  Space,
  Input,
  DatePicker,
  Checkbox,
  Alert,
  Divider,
} from "antd";
import { Api_Url } from "../../../config/config";

const { Option } = Select;
const { TextArea } = Input;

export const ItInventoryUpdateModal = ({ asset, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Reassignment and Search States
  const [reassignType, setReassignType] = useState("Branch");
  const [searching, setSearching] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState(null);

  const REASSIGN_OPTIONS = ["Branch", "Scrap", "Spare"];

  const searchEmployee = async () => {
    const searchValue = form.getFieldValue("EmployeeCodeSearch")?.trim();
    if (!searchValue) {
      message.warning("Please enter an Employee Code to search.");
      return;
    }

    setSearching(true);
    try {
      const response = await fetch(
        `${Api_Url}api/v1/UserProfile/bycode/${encodeURIComponent(
          searchValue
        )}`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user")).accessToken
            }`,
          },
        }
      );
      const result = await response.json();

      if (response.ok && result.data) {
        setEmployeeDetails(result.data);
        form.setFieldsValue({
          NewEmployeeId: result.data.id,
        });
        message.success("New employee found.");
      } else {
        message.info("Employee not found.");
        setEmployeeDetails(null);
        form.setFieldsValue({ NewEmployeeId: null });
      }
    } catch (error) {
      message.error("Error searching employee.");
    } finally {
      setSearching(false);
    }
  };

  const onFinish = async (values) => {
    // Extra validation to ensure date was picked
    if (!values.ReturnDate) {
      message.error("Please select a return date.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        Id: asset.id,
        ReturnDate: values.ReturnDate.format("YYYY-MM-DD"),
        ReassignedId: values.ReassignedId,
        IsScrap: values.ReassignedId === "Scrap" ? true : !!values.IsScrap,
        Remarks: values.Remarks || "",
        NewEmployeeId:
          values.ReassignedId === "Branch" ? values.NewEmployeeId : null,
      };

      const response = await fetch(`${Api_Url}api/v1/ItInventory/${asset.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${
            JSON.parse(sessionStorage.getItem("user")).accessToken
          }`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to update asset");

      message.success("Asset reassigned successfully");
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (val) => {
    setReassignType(val);
    if (val !== "Branch") {
      setEmployeeDetails(null);
      form.setFieldsValue({ NewEmployeeId: null, EmployeeCodeSearch: "" });
    }
  };

  return (
    <Modal
      title={
        <span style={{ fontSize: "20px", fontWeight: "bold" }}>
          Return / Reassign Asset
        </span>
      }
      width={700}
      open={true}
      onCancel={onClose}
      footer={[
        <Space key="footer">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
            disabled={reassignType === "Branch" && !employeeDetails}
          >
            Update Assignment
          </Button>
        </Space>,
      ]}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        // FIX: Removed ReturnDate from initialValues to let user pick manually
        initialValues={{
          ReassignedId: "Branch",
          IsScrap: false,
        }}
      >
        <Alert
          message="Current Assignment"
          description={`Asset ${asset.sapCode} (${asset.serialNumber}) is currently held by ${asset.employeeName}.`}
          type="warning"
          showIcon
          style={{ marginBottom: 20 }}
        />

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="ReturnDate"
              label="Return Date"
              // Mandatory rule ensures form won't submit without selection
              rules={[{ required: true, message: "Please select return date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD-MM-YYYY"
                placeholder="Select Date"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="ReassignedId"
              label="Status After Return"
              rules={[{ required: true }]}
            >
              <Select onChange={handleTypeChange}>
                {REASSIGN_OPTIONS.map((opt) => (
                  <Option key={opt} value={opt}>
                    {opt}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {reassignType === "Branch" && (
          <div
            style={{
              background: "#f0f2f5",
              padding: "15px",
              borderRadius: "8px",
              marginBottom: "20px",
            }}
          >
            <Divider orientation="left" plain>
              New Assignee Details
            </Divider>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  name="EmployeeCodeSearch"
                  label="Search New Employee by Code"
                  rules={[
                    {
                      required: true,
                      message: "Please search for an employee",
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter Employee Code"
                    suffix={
                      <Button
                        type="primary"
                        icon={<i className="fa-solid fa-magnifying-glass"></i>}
                        onClick={searchEmployee}
                        loading={searching}
                      />
                    }
                    onPressEnter={searchEmployee}
                  />
                </Form.Item>
              </Col>
            </Row>
            {employeeDetails && (
              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item label="New Employee Name">
                    <Input
                      value={`${employeeDetails.firstName} ${
                        employeeDetails.lastName || ""
                      }`}
                      disabled
                      style={{ color: "black", fontWeight: "bold" }}
                    />
                  </Form.Item>
                  <Form.Item name="NewEmployeeId" hidden>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </div>
        )}

        {reassignType !== "Branch" && (
          <Alert
            message={`Asset will be moved to ${reassignType} and will be unassigned from ${asset.employeeName}.`}
            type="info"
            showIcon
            style={{ marginBottom: 20 }}
          />
        )}

        <Row gutter={24}>
          <Col span={24}>
            <Form.Item name="IsScrap" valuePropName="checked">
              <Checkbox disabled={reassignType === "Scrap"}>
                Mark as **Scrap** (Item will be decommissioned)
              </Checkbox>
            </Form.Item>
            <Form.Item name="Remarks" label="Notes / Remarks">
              <TextArea
                rows={3}
                placeholder="Describe the condition of the asset or reassignment reason..."
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
